import {FC, Suspense, lazy} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'

import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import AuthGuard from '../../_metronic/auth/AuthGuard'
import {HasPermission} from '../../_metronic/global/functions'
import {shallowEqual, useSelector} from 'react-redux'
import {StoreModel} from '../redux/types'

// Lazy load all page components
const BusinessWrapper = lazy(() =>
  import('../pages/business/AddBusiness').then((module) => ({default: module.BusinessWrapper}))
)
const MyBusinesses = lazy(() => import('../pages/business/MyBusinesses'))
const ViewBusiness = lazy(() => import('../pages/business/ViewBusiness'))
const AllBusinesses = lazy(() => import('../pages/business/AllBusinesses'))
const BusinessModal = lazy(() =>
  import('../pages/business/BusinessModal').then((module) => ({default: module.BusinessModal}))
)

const DepartmentsList = lazy(() => import('../pages/Components/Departments/DepartmentsList'))
const ProfileView = lazy(() => import('../pages/Components/Profiles/ProfileView'))
const ModulesList = lazy(() => import('../pages/Components/Modules/ModulesList'))
const SubscriptionDetail = lazy(
  () => import('../pages/Components/Subscriptions/SubscriptionDetail')
)
const ViewModule = lazy(() => import('../pages/Components/Modules/ViewModule'))
const ProfilesList = lazy(() => import('../pages/Components/Profiles/ProfilesList'))
const RoleList = lazy(() => import('../pages/Components/Roles/RolesList'))
const UsersList = lazy(() => import('../pages/Components/Users/UsersList'))
const InviteLinks = lazy(() => import('../pages/Components/InviteLinks/InviteLinks'))
const SubscriptionsList = lazy(() => import('../pages/Components/Subscriptions/SubscriptionsList'))
const AddSubscription = lazy(() => import('../pages/Components/Subscriptions/AddSubscription'))
const PaymentResponse = lazy(
  () => import('../pages/Components/Subscriptions/Components/PaymentResponse')
)
const ApiKeysList = lazy(() => import('../pages/Components/SendApiKeys/ApiKeysList'))

const DashboardWrapper = lazy(() =>
  import('../pages/dashboard/DashboardWrapper').then((module) => ({
    default: module.DashboardWrapper,
  }))
)
const NewDashboard = lazy(() =>
  import('../pages/dashboard/NewDashboard').then((module) => ({default: module.NewDashboard}))
)

const UpdateProfile = lazy(() => import('../pages/profilesettings/UpdateProfile'))
const ViewProfile = lazy(() => import('../pages/profilesettings/ViewProfile'))

const Forbidden = lazy(() =>
  import('../modules/auth/Forbidden').then((module) => ({default: module.Forbidden}))
)

const NotPart = lazy(() => import('../modules/auth/NotPartOfBusiness').then((module) => ({
  default: module.NotPartOfBusiness,
})))

const NoSubscription = lazy(() => import('../modules/auth/NoSubscription').then((module) => ({
  default: module.NoSubscription,
})))
const Logout = lazy(() => import('../modules/auth').then((module) => ({default: module.Logout})))

const PrivateRoutes = () => {
  const {currentUser} = useSelector(
    (state: StoreModel) => ({
      currentUser: state.globalStates.currentUser,
    }),
    shallowEqual
  )

  return (
    <Routes>
      <Route
        path='business-list/*'
        element={
          <AuthGuard>
            <SuspensedView>
              <BusinessModal />
            </SuspensedView>
          </AuthGuard>
        }
      />
      <Route
        element={
          <AuthGuard>
            <MasterLayout />
          </AuthGuard>
        }
      >
        {/* Redirect to Dashboard after success login/registartion */}
        {/* <Route path='auth/*' element={<Navigate to='/dashboard-new' />} /> */}
        {/* Pages */}

        <Route
          path='business-list'
          element={
            <SuspensedView>
              <BusinessModal />
            </SuspensedView>
          }
        />
        <Route
          path='dashboard-new'
          element={
            <SuspensedView>
              <NewDashboard />
            </SuspensedView>
          }
        />
        {currentUser && currentUser?.business_list.length > 0 && (
          <>
            <Route
              path='dashboard'
              element={
                <SuspensedView>
                  <DashboardWrapper />
                </SuspensedView>
              }
            />
            {/* Components start */}
            <Route
              path='app/departments/all'
              element={
                <SuspensedView>
                  <DepartmentsList />
                </SuspensedView>
              }
            />

            <Route
              path='app/departments/add'
              element={
                <SuspensedView>
                  <DepartmentsList isNew={true} />
                </SuspensedView>
              }
            />

            <Route
              path='app/modules'
              element={
                <SuspensedView>
                  <ModulesList modules_type={'my_modules'} />
                </SuspensedView>
              }
            />

            {HasPermission('module', 'create') && (
              <Route
                path='app/addmodules'
                element={
                  <SuspensedView>
                    <ModulesList modules_type={'all_modules'} />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='/modules/:id'
              element={
                <SuspensedView>
                  <ViewModule />
                </SuspensedView>
              }
            />

            <Route
              path='app/roles/all'
              element={
                <SuspensedView>
                  <RoleList is_new={false} />
                </SuspensedView>
              }
            />

            {HasPermission('role', 'create') && (
              <Route
                path='app/roles/add'
                element={
                  <SuspensedView>
                    <RoleList is_new={true} />
                  </SuspensedView>
                }
              />
            )}

            {HasPermission('role', 'read') && (
              <Route
                path='app/roles/view/:id'
                element={
                  <SuspensedView>
                    <ProfileView />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='app/profiles/all'
              element={
                <SuspensedView>
                  <ProfilesList is_new={false} />
                </SuspensedView>
              }
            />

            {HasPermission('profile', 'create') && (
              <Route
                path='app/profiles/add'
                element={
                  <SuspensedView>
                    <ProfilesList is_new={true} />
                  </SuspensedView>
                }
              />
            )}
            <Route
              path='profile/:id'
              element={
                <SuspensedView>
                  <ProfileView />
                </SuspensedView>
              }
            />

            <Route
              path='app/users/businesusers'
              element={
                <SuspensedView>
                  <UsersList add_user={false} />
                </SuspensedView>
              }
            />

            {HasPermission('user', 'create') && (
              <>
                <Route
                  path='app/users/uploadusers'
                  element={
                    <SuspensedView>
                      <UsersList add_user={false} upload_user={true} />
                    </SuspensedView>
                  }
                />
                <Route
                  path='app/users/inviteusers'
                  element={
                    <SuspensedView>
                      <UsersList add_user={false} upload_user={false} invite_user={true} />
                    </SuspensedView>
                  }
                />
                <Route
                  path='app/addusers'
                  element={
                    <SuspensedView>
                      <UsersList add_user={true} />
                    </SuspensedView>
                  }
                />
              </>
            )}

            <Route
              path='app/invitelinks/all'
              element={
                <SuspensedView>
                  <InviteLinks />
                </SuspensedView>
              }
            />

            <Route
              path='app/users/invitelinks/add'
              element={
                <SuspensedView>
                  <InviteLinks isNew={true} />
                </SuspensedView>
              }
            />

            {(HasPermission('subscription', 'read') || currentUser?.is_pay_by_user) && (
              <Route
                path='app/subscriptions/all'
                element={
                  <SuspensedView>
                    <SubscriptionsList />
                  </SuspensedView>
                }
              />
            )}

            {(HasPermission('subscription', 'create') || currentUser?.is_pay_by_user) && (
              <Route
                path='app/subscriptions/add'
                element={
                  <SuspensedView>
                    <AddSubscription />
                  </SuspensedView>
                }
              />
            )}

            {(HasPermission('subscription', 'read') || currentUser?.is_pay_by_user) && (
              <Route
                path='app/subscriptions/:id'
                element={
                  <SuspensedView>
                    <SubscriptionDetail />
                  </SuspensedView>
                }
              />
            )}

            <Route
              path='subscriptions/payment-success'
              element={
                <SuspensedView>
                  <PaymentResponse />
                </SuspensedView>
              }
            />

            <Route
              path='subscriptions/payment-failed'
              element={
                <SuspensedView>
                  <PaymentResponse />
                </SuspensedView>
              }
            />

            {/* send api key start */}
            {HasPermission('app_keys', 'read') && (
              <Route
                path='app/sendkeys'
                element={
                  <SuspensedView>
                    <ApiKeysList />
                  </SuspensedView>
                }
              />
            )}
            {/* send api key end */}

            {/* components end */}
          </>
        )}

        {/* business settings start */}
        <Route
          path='business/addbusiness'
          element={
            <SuspensedView>
              <BusinessWrapper />
            </SuspensedView>
          }
        />

        <Route
          path='business/mybusinesses'
          element={
            <SuspensedView>
              <MyBusinesses />
            </SuspensedView>
          }
        />

        {HasPermission('business', 'read') && (
          <Route
            path='business/businessdetail'
            element={
              <SuspensedView>
                <ViewBusiness />
              </SuspensedView>
            }
          />
        )}

        {HasPermission('business', 'update') && (
          <Route
            path='business/updatebusiness'
            element={
              <SuspensedView>
                <BusinessWrapper />
              </SuspensedView>
            }
          />
        )}

        <Route
          path='business/allbusinesses'
          element={
            <SuspensedView>
              <AllBusinesses />
            </SuspensedView>
          }
        />
        {/* business settings end */}

        {/* profile settings start */}
        <Route
          path='settings/viewprofile'
          element={
            <SuspensedView>
              <ViewProfile />
            </SuspensedView>
          }
        />
        <Route
          path='settings/viewprofile/:id'
          element={
            <SuspensedView>
              <ViewProfile />
            </SuspensedView>
          }
        />
        <Route
          path='settings/updateprofile'
          element={
            <SuspensedView>
              <UpdateProfile />
            </SuspensedView>
          }
        />
        <Route
          path='settings/updateprofile/:id'
          element={
            <SuspensedView>
              <UpdateProfile />
            </SuspensedView>
          }
        />
        {/* profile settings end */}

        <Route
          path='logout'
          element={
            <SuspensedView>
              <Logout />
            </SuspensedView>
          }
        />
        <Route
          path='forbidden'
          element={
            <SuspensedView>
              <Forbidden />
            </SuspensedView>
          }
        />
        <Route
          path='not_exist'
          element={
            <SuspensedView>
              <NotPart />
            </SuspensedView>
          }
        />
        <Route
          path='no_subscription'
          element={
            <SuspensedView>
              <NoSubscription />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--kt-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
